/**
 * @engine/vfx — VFX preset builders (effects group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts to
 * keep that module focused on content-type / importer / serialization.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Black Hole - Multi-system effect with accretion disk, relativistic jets, and Hawking radiation.
 *
 * Systems:
 * 1. Accretion Disk - Particles spiral inward with vortex motion, HDR orange/white colors
 * 2. Jet Up - Relativistic jet shooting upward, purple/blue HDR
 * 3. Jet Down - Relativistic jet shooting downward
 * 4. Event Horizon - Dark core particles, very dim
 * 5. Hawking Radiation - Rare bright escaping particles
 */

export function createSmokeVFX(name: string = "Smoke"): VFXGraphData {
  const template: VFXGraphData = {
    id: "smoke_effect",
    name: "Smoke",
    nodes: [
      // Spawn
      {
        id: "spawn",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 50 },
        contextId: "ctx_spawn",
      },
      // Initialize
      {
        id: "init_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 3, max: 6 },
        contextId: "ctx_init",
      },
      {
        id: "init_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 10,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init",
      },
      {
        id: "init_noise",
        type: "vfx.init.noise_offset",
        position: { x: 50, y: 250 },
        inputValues: { amplitude: { x: 5, y: 2, z: 5 }, frequency: 1, seed: 0 },
        contextId: "ctx_init",
      },
      {
        id: "init_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 350 },
        inputValues: { direction: { x: 0, y: 1, z: 0 }, speed: 20, spread: 20 },
        contextId: "ctx_init",
      },
      {
        id: "init_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 450 },
        inputValues: { min: 8, max: 15 },
        contextId: "ctx_init",
      },
      {
        id: "init_color",
        type: "vfx.init.color",
        position: { x: 50, y: 550 },
        inputValues: { color: { r: 0.3, g: 0.3, b: 0.35, a: 0.8 } },
        contextId: "ctx_init",
      },
      {
        id: "init_rotation",
        type: "vfx.init.angular_velocity",
        position: { x: 50, y: 650 },
        inputValues: { speed: 20, variance: 40 },
        contextId: "ctx_init",
      },
      // Update
      {
        id: "update_curl",
        type: "vfx.update.curl_noise",
        position: { x: 50, y: 50 },
        inputValues: { intensity: 8, scale: 0.15, speed: 0.3 },
        contextId: "ctx_update",
      },
      {
        id: "update_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 150 },
        inputValues: { coefficient: 0.5 },
        contextId: "ctx_update",
      },
      {
        id: "update_rotation",
        type: "vfx.update.rotation",
        position: { x: 50, y: 250 },
        inputValues: {},
        contextId: "ctx_update",
      },
      {
        id: "update_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.3 },
              { t: 0.2, v: 1 },
              { t: 0.6, v: 1.8 },
              { t: 1, v: 2.5 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update",
      },
      {
        id: "update_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 450 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 0.4, g: 0.4, b: 0.45, a: 0.6 } },
              { t: 0.3, color: { r: 0.35, g: 0.35, b: 0.4, a: 0.5 } },
              { t: 0.7, color: { r: 0.3, g: 0.3, b: 0.35, a: 0.3 } },
              { t: 1, color: { r: 0.25, g: 0.25, b: 0.3, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update",
      },
      // Output
      {
        id: "output",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 0, softParticle: true },
        contextId: "ctx_output",
      },
    ],
    edges: [],
    variables: [],
    contexts: [
      {
        id: "ctx_spawn",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn"],
        collapsed: false,
      },
      {
        id: "ctx_init",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 700 },
        nodeIds: [
          "init_lifetime",
          "init_position",
          "init_noise",
          "init_velocity",
          "init_size",
          "init_color",
          "init_rotation",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update",
        type: "vfx.update",
        position: { x: 50, y: 950 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "update_curl",
          "update_drag",
          "update_rotation",
          "update_size",
          "update_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output",
        type: "vfx.output",
        position: { x: 50, y: 1500 },
        size: { width: 280, height: 120 },
        nodeIds: ["output"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      {
        id: "flow1",
        fromContextId: "ctx_spawn",
        fromPortId: "spawn_event",
        toContextId: "ctx_init",
        toPortId: "spawn_event",
      },
      {
        id: "flow2",
        fromContextId: "ctx_init",
        fromPortId: "particle",
        toContextId: "ctx_update",
        toPortId: "particle",
      },
      {
        id: "flow3",
        fromContextId: "ctx_update",
        fromPortId: "particle",
        toContextId: "ctx_output",
        toPortId: "particle",
      },
    ],
    contextSettings: { ctx_init: { capacity: 300, boundsMode: "auto" } },
    capacity: { maxParticles: 300, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "byAge", blendMode: "alpha" },
  };
  return regenerateGraphIds(template, name);
}

/**
 * Explosion 2D - Detailed cartoon/game explosion with multiple layers.
 *
 * Systems:
 * 1. Flash - Bright white/yellow flash that appears and fades instantly
 * 2. Fireball - Expanding incandescent core (orange → red → dark) 
 * 3. Shockwave Ring - Fast expanding ring of distortion particles
 * 4. Sparks - Hot fragment particles flying outward with gravity
 * 5. Smoke - Dark billowing smoke trails after the flash
 */
